/**
 * Pure drawing functions for canvas rendering.
 * No React hooks — these are stateless helpers called by the canvas renderer.
 */

import { collapseRanges } from '../../utils/automataHelpers';

const LAMBDA = '\u03BB';

/** Format a PDA rule as standard notation: a, Z → AZ  (λ for empty) */
function formatPdaRule(rule) {
  const inp = rule.input || LAMBDA;
  const pop = rule.pop || LAMBDA;
  const push = rule.push || LAMBDA;
  return `${inp}, ${pop} \u2192 ${push}`;
}

/** Format a TM rule as "read → write, D" */
function formatTmRule(rule) {
  const r = rule.read || '\u25A1';
  const w = rule.write || '\u25A1';
  return `${r} \u2192 ${w}, ${rule.direction}`;
}

/** Get label text for a transition (handles DFA/NFA, PDA, and TM) */
function getTransitionLabel(transition) {
  if (transition.tm_rules && transition.tm_rules.length > 0) {
    return transition.tm_rules.map(formatTmRule);
  }
  if (transition.pda_rules && transition.pda_rules.length > 0) {
    return transition.pda_rules.map(formatPdaRule);
  }
  return [collapseRanges(transition.symbols)];
}

/** Draw multi-line label centered at position */
function drawLabel(ctx, lines, x, y, colors) {
  ctx.fillStyle = colors.textPrimary;
  ctx.font = '12px JetBrains Mono';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  if (lines.length <= 1) {
    ctx.fillText(lines[0] || '', x, y);
  } else {
    const lineHeight = 14;
    const totalHeight = lines.length * lineHeight;
    const startY = y - totalHeight / 2 + lineHeight / 2;
    lines.forEach((line, i) => {
      ctx.fillText(line, x, startY + i * lineHeight);
    });
  }
}

/** Measure multi-line label dimensions */
function measureLabel(ctx, lines) {
  ctx.font = '12px JetBrains Mono';
  const lineHeight = 14;
  const maxWidth = Math.max(...lines.map(l => ctx.measureText(l).width));
  const totalHeight = lines.length * lineHeight;
  return { width: maxWidth, height: totalHeight };
}

/**
 * Draw a regular transition (quadratic bezier curve with arrowhead and label).
 * Connection points shift based on curve so bidirectional transitions don't overlap.
 */
export function drawTransition(ctx, from, to, transition, colors, curve = 0, isSelected = false) {
  const symbols = transition.symbols || [];
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  // Calculate perpendicular direction for the curve offset
  const perpX = -dy / dist;
  const perpY = dx / dist;

  // Calculate the midpoint between centers
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;

  // Control point for the curve (offset proportional to distance)
  const curveOffset = curve * dist * 0.5;
  const ctrlX = midX + perpX * curveOffset;
  const ctrlY = midY + perpY * curveOffset;

  // Start point: where line from control point to from.center intersects from's circle
  const fromToCtrlDx = ctrlX - from.x;
  const fromToCtrlDy = ctrlY - from.y;
  const fromToCtrlDist = Math.sqrt(fromToCtrlDx * fromToCtrlDx + fromToCtrlDy * fromToCtrlDy);
  const startX = from.x + (fromToCtrlDx / fromToCtrlDist) * from.radius;
  const startY = from.y + (fromToCtrlDy / fromToCtrlDist) * from.radius;

  // End point: where line from control point to to.center intersects to's circle
  const ctrlToToDx = to.x - ctrlX;
  const ctrlToToDy = to.y - ctrlY;
  const ctrlToToDist = Math.sqrt(ctrlToToDx * ctrlToToDx + ctrlToToDy * ctrlToToDy);
  const endX = to.x - (ctrlToToDx / ctrlToToDist) * to.radius;
  const endY = to.y - (ctrlToToDy / ctrlToToDist) * to.radius;

  // Draw the quadratic bezier curve
  ctx.beginPath();
  ctx.moveTo(startX, startY);
  ctx.quadraticCurveTo(ctrlX, ctrlY, endX, endY);
  ctx.stroke();

  // Draw arrowhead - angle based on direction from control point to end
  const arrowAngle = Math.atan2(endY - ctrlY, endX - ctrlX);
  const arrowLen = 10;
  ctx.fillStyle = ctx.strokeStyle;
  ctx.beginPath();
  ctx.moveTo(endX, endY);
  ctx.lineTo(
    endX - arrowLen * Math.cos(arrowAngle - Math.PI / 6),
    endY - arrowLen * Math.sin(arrowAngle - Math.PI / 6)
  );
  ctx.lineTo(
    endX - arrowLen * Math.cos(arrowAngle + Math.PI / 6),
    endY - arrowLen * Math.sin(arrowAngle + Math.PI / 6)
  );
  ctx.closePath();
  ctx.fill();

  // Draw label offset outward from the actual arc midpoint
  const labelLines = getTransitionLabel(transition);
  if (labelLines.length > 0 && labelLines[0]) {
    // Actual bezier midpoint at t=0.5: (P0 + 2*P1 + P2) / 4
    const arcMidX = (startX + 2 * ctrlX + endX) / 4;
    const arcMidY = (startY + 2 * ctrlY + endY) / 4;
    const labelGap = 10;
    const sign = curveOffset >= 0 ? 1 : -1;
    const nx = perpX * sign;
    const ny = perpY * sign;
    const dims = measureLabel(ctx, labelLines);
    const halfProj = Math.abs(nx) * dims.width / 2 + Math.abs(ny) * dims.height / 2;
    const labelX = arcMidX + nx * (labelGap + halfProj);
    const labelY = arcMidY + ny * (labelGap + halfProj);
    drawLabel(ctx, labelLines, labelX, labelY, colors);
  }
}

/**
 * Draw a self-loop (rotatable ellipse with arrowhead and label).
 */
export function drawSelfLoop(ctx, state, transition, colors, loopAngle, isSelected = false) {
  const angle = loopAngle !== undefined ? loopAngle : -Math.PI / 2;
  const loopWidth = 20;  // short axis (perpendicular to radial)
  const loopHeight = 30; // long axis (along radial)
  const d = state.radius + loopHeight - 5;
  const cx = state.x + Math.cos(angle) * d;
  const cy = state.y + Math.sin(angle) * d;
  const rotation = angle + Math.PI / 2;

  // Draw the rotated ellipse loop
  ctx.beginPath();
  ctx.ellipse(cx, cy, loopWidth, loopHeight, rotation, 0, Math.PI * 2);
  ctx.stroke();

  // Arrowhead at the point where the loop re-enters the state circle
  const r2 = state.radius * state.radius;
  let reEntryT = Math.PI * 0.35; // fallback
  for (let i = 0; i < 72; i++) {
    const t1 = (i / 72) * 2 * Math.PI;
    const t2 = ((i + 1) / 72) * 2 * Math.PI;
    const lx1 = loopWidth * Math.cos(t1), ly1 = loopHeight * Math.sin(t1);
    const p1x = cx + lx1 * Math.cos(rotation) - ly1 * Math.sin(rotation);
    const p1y = cy + lx1 * Math.sin(rotation) + ly1 * Math.cos(rotation);
    const lx2 = loopWidth * Math.cos(t2), ly2 = loopHeight * Math.sin(t2);
    const p2x = cx + lx2 * Math.cos(rotation) - ly2 * Math.sin(rotation);
    const p2y = cy + lx2 * Math.sin(rotation) + ly2 * Math.cos(rotation);
    const d1 = (p1x - state.x) ** 2 + (p1y - state.y) ** 2;
    const d2 = (p2x - state.x) ** 2 + (p2y - state.y) ** 2;
    if (d1 > r2 && d2 <= r2) {
      const sd1 = Math.sqrt(d1), sd2 = Math.sqrt(d2);
      reEntryT = t1 + (t2 - t1) * ((sd1 - state.radius) / (sd1 - sd2));
      break;
    }
  }

  // Tip position on the ellipse at re-entry
  const reLx = loopWidth * Math.cos(reEntryT), reLy = loopHeight * Math.sin(reEntryT);
  const arrowX = cx + reLx * Math.cos(rotation) - reLy * Math.sin(rotation);
  const arrowY = cy + reLx * Math.sin(rotation) + reLy * Math.cos(rotation);

  // Tangent direction at re-entry point
  const tLocalX = -loopWidth * Math.sin(reEntryT);
  const tLocalY = loopHeight * Math.cos(reEntryT);
  const tangentX = tLocalX * Math.cos(rotation) - tLocalY * Math.sin(rotation);
  const tangentY = tLocalX * Math.sin(rotation) + tLocalY * Math.cos(rotation);
  const tangentAngle = Math.atan2(tangentY, tangentX);

  const arrowLen = 10;
  ctx.fillStyle = ctx.strokeStyle;
  ctx.beginPath();
  ctx.moveTo(arrowX, arrowY);
  ctx.lineTo(
    arrowX - arrowLen * Math.cos(tangentAngle - Math.PI / 6),
    arrowY - arrowLen * Math.sin(tangentAngle - Math.PI / 6)
  );
  ctx.lineTo(
    arrowX - arrowLen * Math.cos(tangentAngle + Math.PI / 6),
    arrowY - arrowLen * Math.sin(tangentAngle + Math.PI / 6)
  );
  ctx.closePath();
  ctx.fill();

  // Label at far end of ellipse + outward offset
  const labelLines = getTransitionLabel(transition);
  if (labelLines.length > 0 && labelLines[0]) {
    const labelGap = 10;
    const nx = Math.sin(rotation);
    const ny = -Math.cos(rotation);
    const dims = measureLabel(ctx, labelLines);
    const halfProj = Math.abs(nx) * dims.width / 2 + Math.abs(ny) * dims.height / 2;
    const baseDist = loopHeight + labelGap;
    const labelX = cx + nx * (baseDist + halfProj);
    const labelY = cy + ny * (baseDist + halfProj);
    drawLabel(ctx, labelLines, labelX, labelY, colors);
  }
}
